import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';
import { BusinessPartnerAccess, BusinessPartnerAccessContext, BusinessPartnerAccessMatchType, BusinessPartnerRequiredRoleType } from '../types/business-partner.types';
import { environment } from 'src/environments/environment';

export type BpAccessState = 'pending' | 'denied' | 'error' | BusinessPartnerAccessContext;

interface AccessResponse {
  allowed: boolean;
  access?: BusinessPartnerAccess;
  companyName?: string;
  onboardingMatchType?: BusinessPartnerAccessMatchType | null;
  requiredAccessRoleType?: BusinessPartnerRequiredRoleType | null;
}

@Injectable({ providedIn: 'root' })
export class BusinessPartnerAccessService {
  private readonly state$ = new BehaviorSubject<BpAccessState>('pending');
  readonly accessState$ = this.state$.asObservable();

  private get baseUrl(): string {
    return `${window.location.protocol}//${window.location.hostname}${window.location.port !== '' ? ':8080' : ''}`;
  }

  constructor(private http: HttpClient) {}

  get access(): BusinessPartnerAccess | null {
    const v = this.state$.value;
    if (v === 'pending' || v === 'denied' || v === 'error') return null;
    return v.access;
  }

  get isOnboarded(): boolean {
    const value = this.state$.value;
    return value !== 'pending' && value !== 'denied' && value !== 'error';
  }

  get onboardingMatchType(): BusinessPartnerAccessMatchType | null {
    const value = this.state$.value;
    if (value === 'pending' || value === 'denied' || value === 'error') {
      return null;
    }

    return value.onboardingMatchType ?? null;
  }

  get requiredAccessRoleType(): BusinessPartnerRequiredRoleType | null {
    const value = this.state$.value;
    if (value === 'pending' || value === 'denied' || value === 'error') {
      return null;
    }

    return value.requiredAccessRoleType ?? null;
  }

  get companyName(): string | null {
    const value = this.state$.value;
    if (value === 'pending' || value === 'denied' || value === 'error') {
      return null;
    }

    return value.companyName ?? null;
  }

  get isDenied(): boolean {
    return this.state$.value === 'denied';
  }

  get isError(): boolean {
    return this.state$.value === 'error';
  }

  /** True when the user is allowed to see machine/instrument data. */
  get canViewMachineData(): boolean {
    if (!environment.businessPartnerMode) {
      return true;
    }

    return this.access?.machineData ?? false;
  }

  /** True when the user is allowed to see service history / upcoming services data. */
  get canViewServiceData(): boolean {
    if (!environment.businessPartnerMode) {
      return true;
    }

    return this.access?.serviceData ?? false;
  }

  /** True when device location / bookmark widget should be shown. */
  get canViewDeviceLocation(): boolean {
    if (!environment.businessPartnerMode) {
      return true;
    }

    return this.canViewMachineData || this.canViewServiceData;
  }

  checkAccess(): Observable<void> {
    return this.http.get<AccessResponse>(`${this.baseUrl}/data/businessPartnerAccess`).pipe(
      tap((result) => {
        if (result.allowed && result.access) {
          this.state$.next({
            access: result.access,
            companyName: result.companyName,
            onboardingMatchType: result.onboardingMatchType ?? null,
            requiredAccessRoleType: result.requiredAccessRoleType ?? null,
          });
        } else {
          this.state$.next('denied');
        }
      }),
      map(() => undefined),
      catchError(() => {
        this.state$.next('error');
        return of(undefined);
      }),
    );
  }
}
